import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** Outcome counts for Meta CAPI sends. Skipped = blocked by the kill switch, never sent. */
export interface CapiOutcomeCountsDto {
  sent: number;
  rejected: number;
  skipped: number;
  error: number;
  attempted: number;
  /** Sent ÷ (Sent + Rejected + Error) in percent; null when nothing reached Meta. */
  successRate: number | null;
}

export interface CapiRouteHealthDto extends CapiOutcomeCountsDto {
  actionSource: string;
}

export interface CapiDailyOutcomeDto extends CapiOutcomeCountsDto {
  /** IST day, yyyy-MM-dd. */
  date: string;
}

export interface CapiErrorGroupDto {
  errorCode: number | null;
  errorSubcode: number | null;
  status: 'Rejected' | 'Error';
  actionSource: string;
  count: number;
  sampleMessage: string | null;
  lastSeenAt: string;
}

export type CapiHealthStatus = 'NoData' | 'Off' | 'Failing' | 'Degraded' | 'Healthy';

export interface CapiHealthReportDto {
  from: string;
  to: string;
  health: CapiHealthStatus;
  totals: CapiOutcomeCountsDto;
  lastSentAt: string | null;
  byRoute: CapiRouteHealthDto[];
  topErrors: CapiErrorGroupDto[];
  daily: CapiDailyOutcomeDto[];
}

/** Funnel and money metrics; cost and ROAS fields are null when their divisor is zero. */
export interface AcquisitionFunnelMetricsDto {
  leads: number;
  signUps: number;
  /** Includes existing customers who didn't sign up in the window, so it can exceed signUps. */
  bookedCustomers: number;
  /** Sign-ups who also booked in the window. */
  bookedSignUps: number;
  bookings: number;
  /** Sign-ups who never booked and checked a location outside the service area. */
  outOfAreaSignUps: number;
  customersWithPickup: number;
  firstPickups: number;
  pickups: number;
  gmv: number;
  /** ClearScrap's own cut: platform charges + voucher commission. */
  revenue: number;
  spend: number;
  signUpRate: number | null;
  bookingRate: number | null;
  pickupRate: number | null;
  /** Out-of-area sign-ups as a share of sign-ups. */
  outOfAreaRate: number | null;
  /** Booked ÷ (sign-ups − out-of-area sign-ups). */
  serviceableBookingRate: number | null;
  costPerLead: number | null;
  costPerSignUp: number | null;
  costPerBookedCustomer: number | null;
  /** Spend ÷ first completed pickups. */
  cac: number | null;
  revenueRoas: number | null;
  gmvRoas: number | null;
}

export interface AcquisitionChannelRowDto extends AcquisitionFunnelMetricsDto {
  channelGroup: string;
  label: string;
}

export interface AcquisitionFunnelReportDto {
  from: string;
  to: string;
  attributionWindowDays: number;
  rows: AcquisitionChannelRowDto[];
  totals: AcquisitionFunnelMetricsDto;
  unallocatedSpend: number;
}

export type AdLeaderboardLevel = 'ad' | 'adset' | 'campaign';
export type AdLeaderboardSort = 'spend' | 'leads' | 'pickups' | 'revenueRoas' | 'cac';

export interface AdLeaderboardRowDto extends AcquisitionFunnelMetricsDto {
  id: string;
  name: string | null;
  adsetName: string | null;
  campaignName: string | null;
  cta: 'whatsapp' | 'web' | 'unknown' | 'mixed';
  impressions: number;
  clicks: number;
}

export interface AdLeaderboardReportDto {
  from: string;
  to: string;
  attributionWindowDays: number;
  level: AdLeaderboardLevel;
  items: AdLeaderboardRowDto[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  totals: AcquisitionFunnelMetricsDto;
}

export interface AdLeaderboardQuery {
  from: string;
  to: string;
  level: AdLeaderboardLevel;
  /** 'whatsapp', 'web', or '' for all. */
  cta: string;
  search: string;
  sort: AdLeaderboardSort;
  /** Zero-based. */
  pageIndex: number;
  pageSize: number;
}

export type AdCta = 'whatsapp' | 'web' | 'unknown';

export interface CtaComparisonRowDto extends AcquisitionFunnelMetricsDto {
  cta: AdCta;
  label: string;
  adCount: number;
  impressions: number;
  clicks: number;
  /** Median days from first touch to first completed pickup; null when nobody picked up. */
  medianDaysToFirstPickup: number | null;
}

export interface CtaComparisonCampaignDto {
  id: string;
  name: string | null;
  spend: number;
  /** Ran both WhatsApp-CTA and web-CTA ads: a like-for-like test. */
  hasBothCtas: boolean;
}

export interface CtaComparisonReportDto {
  from: string;
  to: string;
  attributionWindowDays: number;
  campaignId: string | null;
  rows: CtaComparisonRowDto[];
  campaigns: CtaComparisonCampaignDto[];
}

/** Repeat-pickup metrics; pickups are counted up to today (asOf). */
export interface RetentionMetricsDto {
  customers: number;
  customersWithPickup: number;
  customersWithTwoPlusPickups: number;
  customersWithThreePlusPickups: number;
  pickupRate: number | null;
  /** Percent of customers with a first pickup who had a second. */
  repeatRate: number | null;
  threePlusRate: number | null;
  pickups: number;
  /** Pickups per customer who had at least one. */
  averagePickups: number | null;
  firstPickupRevenue: number;
  repeatRevenue: number;
  firstPickupGmv: number;
  repeatGmv: number;
  /** Revenue ÷ all customers in the cohort, including those without a pickup (per sign-up). */
  revenuePerCustomer: number | null;
  /** Revenue ÷ customers with at least one pickup. */
  revenuePerCustomerWithPickup: number | null;
  medianDaysToFirstPickup: number | null;
  medianDaysToSecondPickup: number | null;
}

export interface RetentionRowDto extends RetentionMetricsDto {
  channelGroup: string;
  label: string;
}

export interface RetentionReportDto {
  from: string;
  to: string;
  asOf: string;
  rows: RetentionRowDto[];
  totals: RetentionMetricsDto;
}

/** Value per customer within `days` of their first pickup, for customers who have had that long. */
export interface LtvCheckpointDto {
  days: number;
  matureCustomers: number;
  ltv: number | null;
  /** Revenue minus the configured variable cost per pickup; null when no cost is configured. */
  contributionLtv: number | null;
  averagePickups: number | null;
}

export interface LtvMetricsDto {
  customers: number;
  customersWithPickup: number;
  /** 30, 90, 180 and 365 days. */
  checkpoints: LtvCheckpointDto[];
  isComplete: boolean;
  twelveMonthLtv: number | null;
  twelveMonthContributionLtv: number | null;
  /** True when the 12-month figures are the 90-day value × the projection ratio. */
  isProjected: boolean;
}

export interface LtvCohortRowDto extends LtvMetricsDto {
  /** e.g. "2026-Q1". */
  quarter: string;
  from: string;
  to: string;
}

export interface LtvChannelRowDto extends LtvMetricsDto {
  channelGroup: string;
  label: string;
}

export interface LtvReportDto {
  from: string;
  to: string;
  asOf: string;
  variableCostPerPickup: number | null;
  /** 365-day ÷ 90-day pickups for customers whose first pickup was 1–2 years ago; applied to 90-day LTV. */
  projectionRatio: number | null;
  projectionBasisCustomers: number;
  cohorts: LtvCohortRowDto[];
  channels: LtvChannelRowDto[];
  totals: LtvMetricsDto;
}

/**
 * Acquisition reporting (Control Center → Marketing → Acquisition). Dates are IST calendar days
 * (yyyy-MM-dd), inclusive at both ends.
 */
@Injectable({
  providedIn: 'root'
})
export class MarketingAcquisitionService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/MarketingAcquisition`;

  constructor(private http: HttpClient) { }

  getCapiHealth(from: string, to: string): Observable<CapiHealthReportDto> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<CapiHealthReportDto>(`${this.baseUrl}/capi-health`, { params });
  }

  getFunnel(from: string, to: string): Observable<AcquisitionFunnelReportDto> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<AcquisitionFunnelReportDto>(`${this.baseUrl}/funnel`, { params });
  }

  getAdLeaderboard(query: AdLeaderboardQuery): Observable<AdLeaderboardReportDto> {
    let params = new HttpParams()
      .set('from', query.from)
      .set('to', query.to)
      .set('level', query.level)
      .set('sort', query.sort)
      .set('pageIndex', query.pageIndex)
      .set('pageSize', query.pageSize);
    if (query.cta) {
      params = params.set('cta', query.cta);
    }
    if (query.search.trim()) {
      params = params.set('search', query.search.trim());
    }
    return this.http.get<AdLeaderboardReportDto>(`${this.baseUrl}/ad-leaderboard`, { params });
  }

  getCtaComparison(from: string, to: string, campaignId: string): Observable<CtaComparisonReportDto> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (campaignId) {
      params = params.set('campaignId', campaignId);
    }
    return this.http.get<CtaComparisonReportDto>(`${this.baseUrl}/cta-comparison`, { params });
  }

  getRetention(from: string, to: string): Observable<RetentionReportDto> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<RetentionReportDto>(`${this.baseUrl}/retention`, { params });
  }

  getLtv(from: string, to: string): Observable<LtvReportDto> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<LtvReportDto>(`${this.baseUrl}/ltv`, { params });
  }
}
